// What search offers depends on where it's used: the model screen can run anything on its canvas, the insights screen
// has a graph that can only be viewed, and the other screens have no canvas at all. These rules choose the functions,
// shortcuts and groups for each, and order the results by how well they match
import { FUNCTION, FUNCTION_LEVEL } from '../../model-services/functions';
import { SCREEN } from '../../layouts/layouts-enums';

export type SearchContext = 'model' | 'insights' | 'analysis' | 'home' | 'other';

// the analysis views with a graph and a simulation, which can open the simulation and the side panel
export type AnalysisView = 'actions' | 'people' | 'systems' | 'other';

export const searchContext = (screen: SCREEN | undefined): SearchContext => {
    switch (screen) {
        case SCREEN.MODEL:
        case SCREEN.MODEL_INTRODUCTION:
        case SCREEN.ONBOARDING_MODEL:
            return 'model';
        case SCREEN.INSIGHTS:
            return 'insights';
        case SCREEN.ANALYSIS:
            return 'analysis';
        case SCREEN.ORGANISATION_HOME:
        case SCREEN.PROJECT_HOME:
            return 'home';
        default:
            return 'other';
    }
};

// which analysis view a page is, from its address
export const analysisView = (url: string): AnalysisView => {
    const path = (url || '').split(/[?#]/)[0].replace(/\/+$/, '');
    if (/\/analysis$/.test(path)) return 'actions';
    if (/\/analysis\/people$/.test(path)) return 'people';
    if (/\/analysis\/systems$/.test(path)) return 'systems';
    return 'other';
};

// what looking at a graph needs, so they work on the insights screen's graph as well as the model's
const VIEW_FUNCTIONS = new Set<FUNCTION>([
    FUNCTION.ZOOM_IN, FUNCTION.ZOOM_OUT, FUNCTION.FIT_TO_SCREEN, FUNCTION.RETURN_TO_ORIGINAL, FUNCTION.TOGGLE_VIEW_DIRECTION,
    FUNCTION.MANAGE_LABELS, FUNCTION.RESET_LAYOUT, FUNCTION.CHANGE_LAYOUT, FUNCTION.COLLAPSE_ALL, FUNCTION.EXPAND_ALL,
    FUNCTION.CREATE_GROUPS, FUNCTION.REMOVE_GROUPS, FUNCTION.SWITCH_TO_PAN_MODE, FUNCTION.SWITCH_TO_SELECT_MODE,
    FUNCTION.TOGGLE_MAGNIFYING_GLASS, FUNCTION.TOGGLE_HEATMAP, FUNCTION.DOWNLOAD_MODEL, FUNCTION.RECALCULATE_INSIGHTS,
    FUNCTION.CHANGE_MODEL_TYPE, FUNCTION.APPLY_FILTER, FUNCTION.CLEAR_FILTER, FUNCTION.SCREEN_TOUR,
]);

// functions that act on a canvas or the model being edited, whatever level they're listed at
const CANVAS_FUNCTIONS = new Set<FUNCTION>([FUNCTION.TOGGLE_MAGNIFYING_GLASS, FUNCTION.TOGGLE_HEATMAP, FUNCTION.SCREEN_TOUR, FUNCTION.DELETE_MODEL]);

export interface FunctionContext {
    context: SearchContext;
    view: AnalysisView;
    emptyModel: boolean;            // the model being edited has nothing in it yet
}

// whether search offers a function where it's used
export const offersFunction = (fn: { _key: FUNCTION; level: FUNCTION_LEVEL }, c: FunctionContext): boolean => {
    const key = fn._key;
    // the temporal model is run from the analysis views with a simulation; the side panel is the model's or theirs
    if (key === FUNCTION.TOGGLE_TEMPORAL_MODEL) return c.context === 'analysis' && c.view !== 'other';
    if (key === FUNCTION.TOGGLE_INSIGHTS) return c.context === 'model' || (c.context === 'analysis' && c.view !== 'other');
    // AI builds empty models only, as the toolbar offers it
    if (key === FUNCTION.OPEN_MODELLING_AI) return c.context === 'model' && c.emptyModel;
    if (c.context === 'model') return true;
    // the analysis views explain themselves, as the model and insights screens do
    if (key === FUNCTION.SCREEN_TOUR) return c.context !== 'other' && c.context !== 'home';
    if (c.context === 'insights') return VIEW_FUNCTIONS.has(key) || (fn.level !== FUNCTION_LEVEL.MODEL && fn.level !== FUNCTION_LEVEL.NODE && !CANVAS_FUNCTIONS.has(key));
    // no canvas: the organisation's, the project's and the user's functions
    return fn.level !== FUNCTION_LEVEL.MODEL && fn.level !== FUNCTION_LEVEL.NODE && !CANVAS_FUNCTIONS.has(key);
};

// the shortcuts that help where search is used: all of them on the model's canvas, moving around a graph on the insights screen
const VIEWING_SHORTCUTS = new Set(['SELECTION', 'PAN_GRAPH', 'ZOOM', 'PAN_UP_DOWN', 'FIND_NODE']);

export const offersShortcut = (key: string, context: SearchContext) =>
    context === 'model' || (context === 'insights' && VIEWING_SHORTCUTS.has(key));

export type GroupKind = 'nodes' | 'functions' | 'pages' | 'models' | 'projects' | 'shortcuts' | 'help';

// the groups in the order they matter where search is used
export const GROUP_ORDER: Record<SearchContext, GroupKind[]> = {
    model: ['nodes', 'functions', 'pages', 'models', 'projects', 'shortcuts', 'help'],
    insights: ['nodes', 'functions', 'pages', 'models', 'projects', 'shortcuts', 'help'],
    analysis: ['pages', 'models', 'functions', 'nodes', 'projects', 'help'],
    home: ['models', 'projects', 'pages', 'nodes', 'functions', 'help'],
    other: ['pages', 'models', 'projects', 'functions', 'help'],
};

// with nothing typed yet, a short list of what's likely wanted there, rather than everything
export const EMPTY_GROUPS: Record<SearchContext, GroupKind[]> = {
    model: ['functions', 'pages'],
    insights: ['functions', 'pages'],
    analysis: ['pages', 'models'],
    home: ['models', 'pages'],
    other: ['pages', 'models'],
};

// how well an item matches what's typed: its name before its description, the whole name or its start before a word in
// it, a word's start before anywhere in it. 0 is no match
export const matchScore = (item: { name?: string; description?: string }, query: string): number => {
    const q = query.trim().toLowerCase();
    if (!q) return 1;
    const name = (item.name || '').toLowerCase();
    const description = (item.description || '').toLowerCase();
    const wordStart = (text: string) => text.split(/[^a-z0-9]+/).some(w => w.startsWith(q)) || text.includes(' ' + q);
    if (name === q) return 100;
    if (name.startsWith(q)) return 80;
    if (wordStart(name)) return 60;
    if (name.includes(q)) return 40;
    if (wordStart(description)) return 20;
    if (description.includes(q)) return 10;
    return 0;
};

// the items that match, best first; ties keep their order
export const rankResults = <T extends { name?: string; description?: string }>(items: T[], query: string): T[] => {
    if (!query.trim()) return items;
    return items.map((item, index) => ({ item, index, score: matchScore(item, query) }))
        .filter(x => x.score > 0)
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .map(x => x.item);
};

// with nothing typed yet, the functions most often wanted there, in this order; others are found by typing
export const SUGGESTED_FUNCTIONS: Record<SearchContext, FUNCTION[]> = {
    model: [FUNCTION.SAVE_MODEL, FUNCTION.UNDO, FUNCTION.FIT_TO_SCREEN, FUNCTION.RECALCULATE_INSIGHTS, FUNCTION.EDIT_MODEL_DETAILS, FUNCTION.OPEN_MODELLING_AI, FUNCTION.SCREEN_TOUR],
    insights: [FUNCTION.FIT_TO_SCREEN, FUNCTION.APPLY_FILTER, FUNCTION.CLEAR_FILTER, FUNCTION.RECALCULATE_INSIGHTS, FUNCTION.DOWNLOAD_MODEL, FUNCTION.SCREEN_TOUR],
    analysis: [FUNCTION.TOGGLE_TEMPORAL_MODEL, FUNCTION.CREATE_MODEL, FUNCTION.REFRESH_INSIGHTS, FUNCTION.SCREEN_TOUR],
    home: [FUNCTION.CREATE_MODEL, FUNCTION.CREATE_PROJECT, FUNCTION.MANAGE_USERS, FUNCTION.REFRESH_INSIGHTS],
    other: [FUNCTION.CREATE_ORGANISATION, FUNCTION.EDIT_USER_DETAILS, FUNCTION.CREATE_TICKET],
};

// the suggested functions among those offered, in the suggested order
export const suggestedFunctions = <T extends { _key: FUNCTION }>(offered: T[], context: SearchContext): T[] =>
    SUGGESTED_FUNCTIONS[context].map(k => offered.find(f => f._key === k)).filter((f): f is T => !!f);
